import React from 'react';

import { Box, Grid, Stack, StackProps, Tooltip, Typography, TypographyProps } from '@mui/material';

import { BaseProps } from 'src/components/types';

export interface ImageBlockProps extends BaseProps {
  /**
   * Required. Image element of the image block.
   */
  image?: JSX.Element;
  /**
   * Required. Title text of the image block
   */
  title: JSX.Element | string;

  /**
   * Optional. Caption text of the image block
   */
  caption?: JSX.Element | string;

  // Support for nowrap when a string is directly passed in.
  noWrap?: TypographyProps['noWrap'];

  alignItems?: StackProps['alignItems'];

  fontWeight?: 400 | 500 | 600; // normal, medium, semibold
}

const ImageBlock = ({
  dataTestId,
  image,
  title,
  caption,
  noWrap,
  alignItems,
  fontWeight,
}: ImageBlockProps): JSX.Element => {
  let titleJSX = title;
  if (typeof title === 'string') {
    titleJSX = (
      <Typography noWrap={noWrap} fontWeight={fontWeight}>
        {title}
      </Typography>
    );
    if (noWrap) {
      // Need to show the contents if it's cut off.
      titleJSX = <Tooltip title={title}>{titleJSX}</Tooltip>;
    }
  }

  let captionJSX = caption;
  if (typeof caption === 'string') {
    captionJSX = (
      <Typography variant="body2" color="text.secondary" noWrap={noWrap}>
        {caption}
      </Typography>
    );
    if (noWrap) {
      // Need to show the contents if it's cut off.
      captionJSX = <Tooltip title={caption}>{captionJSX}</Tooltip>;
    }
  }

  return (
    <Grid container wrap="nowrap" spacing={1} alignItems={alignItems} data-testid={dataTestId}>
      {image && (
        <Grid item sx={alignItems ? { display: 'flex', alignItems, marginLeft: '-4px' } : { marginLeft: '-4px' }}>
          {image}
        </Grid>
      )}

      <Grid item zeroMinWidth>
        <Stack direction="column">
          {titleJSX}
          {captionJSX && <Box pt="4px">{captionJSX}</Box>}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ImageBlock;
